import type { ProcessingType } from '../types/imageProcessing';

export const MAX_UPLOAD_BATCH = 10;

export type ImageProcessingNavigationState = {
  pendingFiles?: File[];
  autoProcess?: boolean;
  processingType?: ProcessingType;
  /** Open the pending-review queue (from dashboard badge, voice, etc.) */
  focusPendingReviews?: boolean;
  /** Load a saved processing record into the results panel */
  reviewProcessingId?: string | number;
};

export const ACCEPTED_UPLOAD_EXTENSIONS = new Set([
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif', 'pdf', 'tiff', 'tif',
]);

const SPREADSHEET_EXTENSIONS = new Set(['csv', 'tsv', 'txt', 'xlsx', 'xls']);

export const isSpreadsheetUploadFile = (file: File): boolean => {
  const type = (file.type || '').toLowerCase();
  if (
    type.includes('spreadsheet')
    || type === 'text/csv'
    || type === 'text/plain'
    || type === 'application/vnd.ms-excel'
    || type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ) {
    return true;
  }
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  return SPREADSHEET_EXTENSIONS.has(extension);
};

export const isAcceptedUploadFile = (file: File): boolean => {
  const type = (file.type || '').toLowerCase();
  if (type.startsWith('image/') || type === 'application/pdf') {
    return true;
  }
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  return ACCEPTED_UPLOAD_EXTENSIONS.has(extension);
};

export const buildImageProcessingNavigationState = (
  files: File[],
  processingType: ProcessingType = 'auto',
): ImageProcessingNavigationState | null => {
  const accepted = files.filter(isAcceptedUploadFile);
  if (accepted.length === 0) {
    return null;
  }
  return {
    pendingFiles: accepted.slice(0, MAX_UPLOAD_BATCH),
    autoProcess: true,
    processingType,
  };
};

export const resolveBusinessId = (business?: { id?: number } | null): number | null => {
  if (business?.id) {
    return business.id;
  }
  const stored = localStorage.getItem('businessId');
  if (!stored) {
    return null;
  }
  const parsed = parseInt(stored, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

export const buildImageFormData = (
  file: File,
  processingType: ProcessingType,
  businessId: number,
  userId?: number,
) => {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('type', processingType);
  formData.append('business_id', String(businessId));
  formData.append('context', JSON.stringify({
    businessId,
    userId,
  }));
  return formData;
};

type UploadApiError = {
  code?: string;
  message?: string;
  response?: { status?: number; data?: { error?: string; message?: string } };
};

export const isTransientNetworkError = (error: unknown): boolean => {
  const apiError = error as UploadApiError;
  return apiError.code === 'ERR_NETWORK' || apiError.message?.includes('Network Error') === true;
};

export const resolveUploadErrorMessage = (
  error: unknown,
  fallback = 'Failed to process document',
): string => {
  const apiError = error as UploadApiError;

  if (isTransientNetworkError(error)) {
    return 'Cannot reach the server. Make sure the backend is running (npm run dev, port 5001), then try again.';
  }
  if (apiError.response?.status === 429) {
    return 'API rate limit exceeded. Please wait a moment and try again.';
  }
  if (apiError.message?.toLowerCase().includes('rate limit')) {
    return 'OpenAI API rate limit reached. The system will use OCR processing instead.';
  }
  if (apiError.response?.status === 409) {
    return (
      apiError.response?.data?.error ||
      apiError.response?.data?.message ||
      'This image was already processed. Try a different file.'
    );
  }
  if (apiError.response?.data?.error) {
    return apiError.response.data.error;
  }
  if (apiError.response?.data?.message) {
    return apiError.response.data.message;
  }
  if (apiError.message) {
    return apiError.message;
  }
  return fallback;
};

const NETWORK_RETRY_DELAY_MS = 1500;

export const withUploadNetworkRetry = async <T>(
  operation: () => Promise<T>,
  retries = 1,
): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    if (retries > 0 && isTransientNetworkError(error)) {
      await new Promise((resolve) => setTimeout(resolve, NETWORK_RETRY_DELAY_MS));
      return withUploadNetworkRetry(operation, retries - 1);
    }
    throw error;
  }
};
